import React, { useEffect, useState } from "react";
import { useAppSelector, useAppTranslation } from "@/utils";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, Form, Space, Typography, Upload, useMessagedAsyncFn } from "@payping/web-components";
import { DashboardChecksTypeEnum, FileTypeEnum, UserDocumentAdminModel, fileApis, uploadApis } from "@payping/api";
import { Close } from "@payping/icons";
import EditActionButtons from "@/components/mobile/settings/EditActionButtons";
import styles from "@/assets/scss/profile/Profile.module.scss";
import { UploadChangeParam, UploadFile } from "@payping/web-components/components/Upload/types";
import { Upload as UploadIcon } from "@payping/icons/icons";
import { getFileName, getFileType } from "@payping/utils/string";
import { BusinessInformation } from "@/pages/Settings/Business/type";
import { DashboardRoutes } from "@/router/constant";
import { isMobileBusinessRegisterURL } from "@/utils/isRegisterURL";
import { useHandleNavigationURL } from "@/mobile/Settings/Business/utils";
import { selectAtom } from "jotai/utils";
import { settingsBusinessAtom } from "@/mobile/Settings/state";
import { useAtom } from "jotai";

const mobileFormEditAtom = selectAtom(settingsBusinessAtom, (val) => val.mobileFormEdit);

const MobileEditOfficialBusinessDocument: React.FC = () => {
  const isRegisterURL = isMobileBusinessRegisterURL();
  const [mobileFormEditState] = useAtom(mobileFormEditAtom);

  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<BusinessInformation>();
  const checks = useAppSelector((state) => state.user.checks);
  const isBusinessDisabled = checks?.businessStatus === DashboardChecksTypeEnum.Confirmed;
  const businessDocuments = useAppSelector((state) => state.user.businessInfo?.documents);

  const [businessCertificateList, setBusinessCertificateList] = useState<UploadFile[]>([]);
  const [introductionLetterList, setIntroductionLetterList] = useState<UploadFile[]>([]);
  const [permissionList, setPermissionList] = useState<UploadFile[]>([]);
  const [removeDocCalled, setRemoveDocCalled] = useState<boolean>(false);

  const [, removeDocument] = useMessagedAsyncFn(
    (id) => fileApis.removeDocumentWithIdWithCancellationTokenDelete(id),
    [],
    undefined,
    {
      useProgress: true,
    },
  );

  useEffect(() => {
    if (businessDocuments?.[0]) {
      const certificateServerName = businessDocuments[0].serverName || "";
      const certificateName = certificateServerName
        ?.split("/")
        [certificateServerName.split("/").length - 1].split("?")[0];
      setBusinessCertificateList([
        {
          name: `${getFileName(certificateName)}`,
          size: 0,
          type: getFileType(certificateName),
          uid: "1",
          url: certificateServerName,
        },
      ]);
    }
    if (businessDocuments?.[1]) {
      const introductionLetterServerName = businessDocuments[1].serverName || "";
      const introductionLetterName = introductionLetterServerName
        ?.split("/")
        [introductionLetterServerName.split("/").length - 1].split("?")[0];
      setIntroductionLetterList([
        {
          name: `${getFileName(introductionLetterName)}`,
          size: 0,
          type: getFileType(introductionLetterName),
          uid: "1",
          url: introductionLetterServerName,
        },
      ]);
    }
    if (businessDocuments?.[2]) {
      const permissionServerName = businessDocuments[2].serverName || "";
      const permissionName = permissionServerName?.split("/")[permissionServerName.split("/").length - 1].split("?")[0];
      setPermissionList([
        {
          name: `${getFileName(permissionName)}`,
          size: 0,
          type: getFileType(permissionName),
          uid: "1",
          url: permissionServerName,
        },
      ]);
    }
    return () => {
      setBusinessCertificateList([]);
      setIntroductionLetterList([]);
      setPermissionList([]);
    };
  }, [businessDocuments, form]);

  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: BusinessInformation) => {
      const { businessCertificate, introductionLetter, permission } = values;
      const documentsFileList = [
        ...(businessCertificate?.fileList || []),
        ...(introductionLetter?.fileList || []),
        ...(permission?.fileList || []),
      ];
      if (removeDocCalled && businessDocuments) {
        await Promise.all(
          businessDocuments.map(async (doc: UserDocumentAdminModel) => {
            await removeDocument(doc.id);
          }),
        );
      }
      if (documentsFileList) {
        await Promise.all(
          documentsFileList.map(async (doc: UploadFile) => {
            const fileName = doc.url ? doc.url.split("/").pop() : doc.response;
            await fileApis.addFileWithRequestModelWithCancellationTokenPost({
              fileName,
              type: FileTypeEnum.EstablishmentCertificate,
            });
          }),
        );
      }
      if (isRegisterURL) {
        navigate(DashboardRoutes.register.bank.url);
      } else {
        navigate(DashboardRoutes.settings.business.root.url);
      }
    },
    [removeDocCalled, businessDocuments],
  );

  // Custom Hooks
  const { handleNavigationURL } = useHandleNavigationURL();

  return (
    <>
      <Drawer
        placement={"bottom"}
        open={true}
        height={"100%"}
        title={
          isRegisterURL
            ? t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")
            : `${t("edit")} / ${t("settings.business.certificateOfEstablishmentAndLetterOfIntroduction")}`
        }
        closeIcon={false}
        extra={<Button type={"text"} icon={<Close width={14} height={14} />} onClick={handleNavigationURL} />}
        footer={<EditActionButtons loading={loadingSubmit} onAccept={form.submit} />}
      >
        <Space direction={"vertical"}>
          <Form<BusinessInformation> onFinish={onSubmit} form={form} initialValues={{ documents: businessDocuments }}>
            {mobileFormEditState.isOfficialSubmited ? (
              <>
                <Form.Item name={"businessCertificate"} className={styles.flex}>
                  <Upload
                    crop={false}
                    listType={"picture-card"}
                    requestFunction={uploadApis.miscWithFilePost}
                    onChange={(info: UploadChangeParam): void => setBusinessCertificateList(info.fileList)}
                    fileList={businessCertificateList}
                    showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                    onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                    allowedExtensions={["jpg", "jpeg", "png"]}
                    className={styles.flex}
                  >
                    {businessCertificateList.length ? null : (
                      <div>
                        <UploadIcon width={"30px"} />
                        <div className={"ant-upload-text"}>
                          {t("settings.business.upload.certificateOfEstablishment")}
                        </div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
                <Form.Item name="introductionLetter" className={styles.flex}>
                  <Upload
                    crop={false}
                    listType={"picture-card"}
                    requestFunction={uploadApis.miscWithFilePost}
                    onChange={(info: UploadChangeParam): void => setIntroductionLetterList(info.fileList)}
                    fileList={introductionLetterList}
                    showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                    onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                    allowedExtensions={["jpg", "jpeg", "png"]}
                    className={styles.flex}
                  >
                    {introductionLetterList.length ? null : (
                      <div>
                        <UploadIcon width={"30px"} />
                        <div className={"ant-upload-text"}>{t("settings.business.upload.introductionLetter")}</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
                <Form.Item name="permission" className={styles.flex}>
                  <Upload
                    crop={false}
                    listType={"picture-card"}
                    requestFunction={uploadApis.miscWithFilePost}
                    onChange={(info: UploadChangeParam): void => setPermissionList(info.fileList)}
                    fileList={permissionList}
                    showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                    onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                    allowedExtensions={["jpg", "jpeg", "png"]}
                    className={styles.flex}
                  >
                    {permissionList.length ? null : (
                      <div>
                        <UploadIcon width={"30px"} />
                        <div className={"ant-upload-text"}>{t("settings.business.upload.permission")}</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </>
            ) : (
              <Form.Item name="permission" className={styles.flex}>
                <Upload
                  crop={false}
                  listType={"picture-card"}
                  requestFunction={uploadApis.miscWithFilePost}
                  onChange={(info: UploadChangeParam): void => setPermissionList(info.fileList)}
                  fileList={permissionList}
                  showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
                  onRemove={() => (isBusinessDisabled ? false : setRemoveDocCalled(true))}
                  allowedExtensions={["jpg", "jpeg", "png"]}
                  className={styles.flex}
                >
                  {permissionList.length ? null : (
                    <div>
                      <UploadIcon width={"30px"} />
                      <div className={"ant-upload-text"}>{t("settings.business.upload.permission")}</div>
                    </div>
                  )}
                </Upload>
              </Form.Item>
            )}
          </Form>
          <Typography.Text type={"secondary"}>{t("mobileEditPersonal.nameDesc")}</Typography.Text>
        </Space>
      </Drawer>
    </>
  );
};

export default MobileEditOfficialBusinessDocument;
